import type { DraftView } from '@tabel/shared/schemas/rules';
import type { PlanListItem } from '@tabel/shared/schemas/rules-plan';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bot, ExternalLink, MoreHorizontal, PenLine, Send, Trash2, UserRoundPlus } from 'lucide-react';
import { useEffect, useId, useState, useSyncExternalStore } from 'react';
import { useToast } from '../../components/ErrorToast';
import {
  AlertDialog,
  Button,
  Callout,
  Dropdown,
  IconButton,
  MenuItem,
  Modal,
  inputCls,
  textareaCls,
} from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { ruDate, ruDateTime } from '../../lib/format';
import { useRole } from '../../lib/role';
import { PLAN_LABEL, sayPlan } from './plan-texts';
import { DRAFT, LABEL, T, say } from './texts';

/*
 * Полоса черновика (004, T066; contracts/ui.md, «Полоса черновика», окна «Передать черновик», «Отменить черновик?»;
 * FR-028, FR-030; таблица прав): кто ведёт черновик и сколько в нём изменений; «Что изменится» — всем, кто видит
 * черновик; «Опубликовать», «Передать черновик», «Отменить черновик» — ведущему (администратор правил);
 * «Взять черновик» — администратору правил, если ведущего нет; администратор системы отменяет чужой черновик только
 * с причиной. На телефоне кнопки уходят в «…» полосы, кроме «Опубликовать». Пока открыт план ИИ-агента (005), страница
 * передаёт публикацию по плану и подпись «Опубликовать по плану» или не передаёт публикацию вовсе. Есть действующий
 * план агента к этому черновику, и он не открыт — строка «К черновику есть план ИИ-агента №N…» и «Открыть план»
 * (005, FR-065): без ссылки агента план иначе не найти.
 */

const PHONE_QUERY = '(max-width: 639px)';
function readPhone(): boolean {
  if (typeof window.matchMedia === 'function') return window.matchMedia(PHONE_QUERY).matches;
  return window.innerWidth < 640; // jsdom: matchMedia нет
}
function subscribePhone(onChange: () => void): () => void {
  window.addEventListener('resize', onChange);
  const mq = typeof window.matchMedia === 'function' ? window.matchMedia(PHONE_QUERY) : null;
  mq?.addEventListener?.('change', onChange);
  return () => {
    window.removeEventListener('resize', onChange);
    mq?.removeEventListener?.('change', onChange);
  };
}
/** Телефон — до 640 px. */
function usePhone(): boolean {
  return useSyncExternalStore(subscribePhone, readPhone, () => false);
}

/** Окно «Передать черновик»: другой администратор правил объекта (`DraftView.transferTo`). */
function TransferDialog({ open, draft, onClose }: { open: boolean; draft: DraftView; onClose: () => void }) {
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const id = useId();
  const [to, setTo] = useState('');
  useEffect(() => {
    if (open) setTo('');
  }, [open]);
  // Кому передать — сервер отдаёт ведущему в черновике (`DraftView.transferTo`).
  const list = draft.transferTo ?? [];
  const transfer = useMutation({
    mutationFn: (toUserId: number) => api.transfer({ etag: draft.etag, toUserId }),
    onSuccess: (_, toUserId) => {
      const name = list.find((a) => a.userId === toUserId)?.name ?? '';
      showInfo(say.draftTransferred(name));
      onClose();
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
    },
    onError: (e) => showError(e, { screen: 'Rules', action: 'transfer draft' }),
  });
  return (
    <Modal open={open} onClose={onClose} title={LABEL.transfer} width="w-[420px]">
      <p className="mb-4 text-[13px] text-ink-2">{T.transferText}</p>
      <label htmlFor={`${id}-to`} className="mb-1.5 block text-[13px] font-medium text-ink">
        {LABEL.admin}
      </label>
      <select
        id={`${id}-to`}
        data-autofocus
        aria-label={LABEL.admin}
        value={to}
        onChange={(e) => setTo(e.target.value)}
        disabled={list.length === 0}
        className={inputCls}
      >
        <option value="" />
        {list.map((a) => (
          <option key={a.userId} value={String(a.userId)}>
            {a.name}
          </option>
        ))}
      </select>
      {list.length === 0 && <p className="mt-1 text-[12px] text-ink-3">{T.noAdmins}</p>}
      <div className="mt-6 flex flex-wrap items-center justify-end gap-2 max-sm:[&>button]:flex-auto">
        <Button onClick={onClose}>{LABEL.cancel}</Button>
        <Button
          variant="primary"
          disabled={to === '' || transfer.isPending}
          onClick={() => transfer.mutate(Number(to))}
        >
          {LABEL.transferDo}
        </Button>
      </div>
    </Modal>
  );
}

/** Окно «Отменить черновик?»: число изменений; у администратора системы — причина. */
function DiscardDialog({
  open,
  draft,
  needReason,
  onClose,
}: {
  open: boolean;
  draft: DraftView;
  needReason: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const id = useId();
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const discard = useMutation({
    mutationFn: () => api.discard(draft.etag, needReason ? reason.trim() : undefined),
    onSuccess: () => {
      showInfo(DRAFT.discarded);
      onClose();
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
    },
    onError: (e) => showError(e, { screen: 'Rules', action: 'discard draft' }),
  });
  const ready = !needReason || reason.trim().length > 0;
  return (
    <AlertDialog
      open={open}
      onClose={onClose}
      title={DRAFT.discardTitle}
      icon={Trash2}
      description={
        <>
          {say.discardText(draft.changes.length)}
          {needReason && (
            <span className="mt-3 block">
              <label htmlFor={`${id}-reason`} className="mb-1.5 block text-[13px] font-medium text-ink">
                {LABEL.reason}
              </label>
              <textarea
                id={`${id}-reason`}
                aria-label={LABEL.reason}
                rows={3}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className={textareaCls}
              />
            </span>
          )}
        </>
      }
    >
      <Button data-autofocus onClick={onClose}>
        {DRAFT.keep}
      </Button>
      <Button variant="danger" disabled={!ready || discard.isPending} onClick={() => discard.mutate()}>
        {DRAFT.discard}
      </Button>
    </AlertDialog>
  );
}

interface Action {
  readonly key: string;
  readonly label: string;
  readonly icon?: typeof PenLine;
  readonly run: () => void;
  readonly danger?: boolean;
}

export function DraftBar({
  draft,
  onChanges,
  onPublish,
  publishLabel = LABEL.publish,
  plan,
}: {
  draft: DraftView;
  /** «Что изменится» — вкладка со списком изменений и дней. */
  onChanges: () => void;
  /** «Опубликовать» — окно публикации (US3); без него кнопки нет. */
  onPublish?: () => void;
  /** Подпись кнопки публикации: при открытом плане ИИ-агента — «Опубликовать по плану» (005). */
  publishLabel?: string;
  /** Действующий план агента к этому черновику, если он не открыт (FR-065), и время объекта для его срока. */
  plan?: {
    readonly item: PlanListItem;
    readonly tzOffset: string;
    readonly onOpen: (planId: number) => void;
  };
}) {
  const { role } = useRole();
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const phone = usePhone();
  const [dialog, setDialog] = useState<'transfer' | 'discard' | null>(null);
  const leads = draft.isLeader && role === 'rules_admin';
  const take = useMutation({
    mutationFn: () => api.take({ etag: draft.etag }),
    onSuccess: () => {
      showInfo(T.draftTaken);
      void qc.invalidateQueries({ queryKey: rulesKeys.all });
    },
    onError: (e) => showError(e, { screen: 'Rules', action: 'take draft' }),
  });

  const text =
    draft.leader === null
      ? T.draftNoLeader
      : draft.isLeader
        ? say.draftMine(draft.changes.length)
        : say.draftOther(draft.leader.name);

  const actions: Action[] = [
    { key: 'changes', label: LABEL.tabChanges, run: onChanges },
    ...(leads
      ? [
          { key: 'transfer', label: LABEL.transfer, icon: UserRoundPlus, run: () => setDialog('transfer') },
          {
            key: 'discard',
            label: DRAFT.discard,
            icon: Trash2,
            run: () => setDialog('discard'),
            danger: true,
          },
        ]
      : []),
    ...(!draft.isLeader && role === 'sysadmin'
      ? [
          {
            key: 'discard',
            label: DRAFT.discard,
            icon: Trash2,
            run: () => setDialog('discard'),
            danger: true,
          },
        ]
      : []),
    ...(draft.leader === null && role === 'rules_admin'
      ? [{ key: 'take', label: LABEL.take, run: () => take.mutate() }]
      : []),
  ];
  const publish = leads && onPublish;
  const folded = phone && (actions.length > 1 || publish);

  return (
    <section aria-label={LABEL.draftBar}>
      <Callout tone="accent" icon={PenLine}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <p className="min-w-0 flex-1 basis-[240px] font-medium text-ink">{text}</p>
          <div className="flex flex-wrap items-center gap-2 max-sm:w-full max-sm:[&>button]:flex-1">
            {publish && folded && (
              <Button size={phone ? 'md' : 'sm'} variant="primary" icon={Send} onClick={onPublish}>
                {publishLabel}
              </Button>
            )}
            {folded ? (
              <Dropdown
                align="end"
                width="w-[240px]"
                trigger={({ open, toggle }) => (
                  <IconButton
                    icon={MoreHorizontal}
                    label={LABEL.draftMore}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    onClick={toggle}
                    className="border border-line bg-surface"
                  />
                )}
              >
                {(close) =>
                  actions.map((a) => (
                    <MenuItem
                      key={a.key}
                      icon={a.icon}
                      danger={a.danger}
                      onClick={() => {
                        close();
                        a.run();
                      }}
                    >
                      {a.label}
                    </MenuItem>
                  ))
                }
              </Dropdown>
            ) : (
              <>
                {actions.map((a) => (
                  <Button
                    key={a.key}
                    size="sm"
                    variant={a.key === 'changes' ? 'secondary' : 'ghost'}
                    disabled={a.key === 'take' && take.isPending}
                    onClick={a.run}
                  >
                    {a.label}
                  </Button>
                ))}
                {publish && (
                  <Button size={phone ? 'md' : 'sm'} variant="primary" icon={Send} onClick={onPublish}>
                    {publishLabel}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
        {plan && (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-accent/15 pt-2">
            <p className="flex min-w-0 flex-1 basis-[240px] items-start gap-2 text-[13px] text-ink">
              <Bot size={14} strokeWidth={1.75} className="mt-[3px] shrink-0 text-accent" aria-hidden />
              <span className="min-w-0">
                {sayPlan.draftHas(
                  plan.item.planId,
                  ruDate(plan.item.validFrom),
                  ruDateTime(plan.item.expiresAt, plan.tzOffset),
                )}
              </span>
            </p>
            <Button
              size={phone ? 'md' : 'sm'}
              icon={ExternalLink}
              className="max-sm:w-full"
              onClick={() => plan.onOpen(plan.item.planId)}
            >
              {PLAN_LABEL.open}
            </Button>
          </div>
        )}
      </Callout>
      {leads && <TransferDialog open={dialog === 'transfer'} draft={draft} onClose={() => setDialog(null)} />}
      {(leads || role === 'sysadmin') && (
        <DiscardDialog
          open={dialog === 'discard'}
          draft={draft}
          needReason={role === 'sysadmin'}
          onClose={() => setDialog(null)}
        />
      )}
    </section>
  );
}
